import { useCallback, useEffect, useState, useRef } from "react";
import { startCase } from "lodash";
import { Box, ButtonBase, Tooltip, Menu, MenuItem, CircularProgress } from "@mui/material";
import { Text } from "@imminently/react/ui/core/text";
import { ActionIcon, getStatusColor } from "@/components/Action";
import { Task, TaskHistory, TaskStatus, getTaskStatusFromStateName, getStateNameFromTaskStatus } from "@/resources/types";
import { css, Theme } from "@/theme";
import moment from "moment";
import * as Icons from "@/assets/icons";
import { useTranslation } from "react-i18next";

// Time to ignore external updates after a user click (ms)
const CLICK_DEBOUNCE_MS = 1000;

// Task definition types (matching the settings structure)
type TaskState = {
  id: string;
  name: string;
  colour: string;
  order: number;
};

type TaskDefinition = {
  id: string;
  name: string;
  icon: string;
  states: TaskState[];
};

const styles = {
  container: (theme: Theme) => css`
    border-radius: 1rem;
    padding-bottom: 1rem;
  `,
  taskItem: (small: boolean, theme?: Theme) => css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: ${small ? "0.5rem" : "0.75rem 1rem"};
    border-radius: 0.5rem;
    min-width: ${small ? "2.5rem" : "11rem"};
    justify-content: flex-start;
    text-align: start;

    &:focus-visible, &:focus, &:focus-within {
      box-shadow: inset 0 0 0px 2px;
    }
  `,
};

export interface PatientTaskButtonProps {
  value: Task;
  onClick: (task: Task) => void;
  small?: boolean;
  disabled?: boolean;
  taskDefinition?: TaskDefinition;
}

const getNextState = (currentState: TaskState, states: TaskState[]): TaskState => {
  // Sort by order - use slice to avoid mutating the original array
  const sortedStates = [...states].sort((a, b) => a.order - b.order);
  const currentIndex = sortedStates.findIndex(s => s.id === currentState.id);
  const nextIndex = (currentIndex + 1) % sortedStates.length;
  return sortedStates[nextIndex];
};

const getStateFromStatus = (status: TaskStatus, states: TaskState[]): TaskState => {
  // State names are now translation keys (e.g., "not_requested") that match TaskStatus values
  // Try to find state by matching name directly to status
  const matchingState = states.find(s => s.name === status);
  if (matchingState) return matchingState;
  
  // Fallback: try legacy conversion for backward compatibility
  const stateName = getStateNameFromTaskStatus(status);
  return states.find(s => s.name === stateName) || states[0];
};

const getStatusFromState = (state: TaskState): TaskStatus => {
  // State names are now translation keys that match TaskStatus format (e.g., "not_requested")
  // Use the state name directly as the status
  return state.name as TaskStatus;
};

export const TaskTooltip = ({ taskDefinition, currentState, history }: {
  taskDefinition: TaskDefinition;
  currentState: TaskState;
  history?: TaskHistory[]
}) => {
  const { t } = useTranslation();

  const statusAndTypeBlock = (
    <>
      <Text>{t(`tasks.${taskDefinition.name}`)}</Text>
      <Text variant="caption" bold>{t(`tasks.${currentState.name}`)}</Text>
    </>
  );

  const lastHistoryItem = history?.[0];
  //If there is no history, returns the type and status of the task
  if (!lastHistoryItem) return statusAndTypeBlock;

  const historyItem = lastHistoryItem;
  const displayUserName = historyItem.actingAsName || lastHistoryItem.userName;
  //Already present difference between now and that time in seconds
  const timeElapsed = Math.floor(moment().diff(moment(lastHistoryItem.when)) / 1000);
  const hourElapsed = Math.floor(timeElapsed / 3600);
  const minutesElapsed = Math.floor(timeElapsed % 3600 / 60);
  const historyBlock = (
    <>
      <Text>When: {moment(lastHistoryItem.when).format("HH:mm DD/MM/YYYY")}</Text>
      <Text>By: {displayUserName}</Text>
      <Text>Time elapsed: {`${hourElapsed}:${minutesElapsed}`} ago</Text>
    </>
  );
  return (
    <>
      {statusAndTypeBlock}
      {historyBlock}
    </>
  );
};

const PatientTaskButton = (props: PatientTaskButtonProps) => {
  const { value: task, onClick, small = false, disabled, taskDefinition } = props;
  const { t } = useTranslation();

  // Track last user interaction time to debounce external updates
  const lastClickTimeRef = useRef<number>(0);

  // If no task definition provided, fall back to old behavior
  if (!taskDefinition) {
    const [status, setStatus] = useState<TaskStatus>(task.status);

    const incrementStatus = useCallback(() => {
      lastClickTimeRef.current = Date.now();
      setStatus((prevStatus) => {
        // Fallback to old hardcoded behavior
        let newStatus: TaskStatus;
        switch (prevStatus) {
          case "requested":
            newStatus = "done";
            break;
          case "done":
            newStatus = "not_requested";
            break;
          case "ready":
            newStatus = "done";
            break;
          default:
            newStatus = "requested";
        }
        onClick({ ...task, status: newStatus });
        return newStatus;
      });
    }, [task, onClick]);

    // update status if changed externally (ie from pubsub)
    // Ignore updates for a short period after user clicks to prevent race conditions
    useEffect(() => {
      const timeSinceClick = Date.now() - lastClickTimeRef.current;
      if (timeSinceClick > CLICK_DEBOUNCE_MS) {
        setStatus(task.status);
      }
    }, [task.status]);

    // empty string will cause tooltip to not show
    const title = small ? "" : "";
    const colors = getStatusColor(status);

    // want tooltip at top level otherwise we can get weird layout shift
    return (
      <Tooltip arrow title={title}>
        <ButtonBase role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); incrementStatus(); }} css={styles.taskItem(small)} sx={colors} disabled={disabled}>
          <ActionIcon key={task.type} action={task} />
          {!small && (
            <Box sx={{ display: "inline-block" }}>
              <Text variant={status === "not_requested" ? "button" : "buttonSmall"}>{task.type}</Text>
              {status !== "not_requested" && <Text bold variant="buttonSmall">{startCase(status)}</Text>}
            </Box>
          )}
        </ButtonBase>
      </Tooltip>
    );
  }

  // New behavior with task definition
  const [currentState, setCurrentState] = useState<TaskState>(() =>
    getStateFromStatus(task.status, taskDefinition.states)
  );
  const [isLoading, setIsLoading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  // Sort states by order for display
  const sortedStates = [...taskDefinition.states].sort((a, b) => a.order - b.order);

  const changeState = useCallback((newState: TaskState) => {
    lastClickTimeRef.current = Date.now();
    setIsLoading(true);
    setCurrentState(newState);
    const newStatus = getStatusFromState(newState);
    onClick({ ...task, status: newStatus });
    // Reset loading after a short delay to show feedback
    setTimeout(() => setIsLoading(false), 300);
  }, [task, onClick]);

  const incrementStatus = useCallback(() => {
    const nextState = getNextState(currentState, taskDefinition.states);
    changeState(nextState);
  }, [currentState, taskDefinition.states, changeState]);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleStateSelect = useCallback((selectedState: TaskState) => {
    handleMenuClose();
    changeState(selectedState);
  }, [changeState, handleMenuClose]);


  // update state if changed externally (ie from pubsub)
  // Ignore updates for a short period after user clicks to prevent race conditions
  useEffect(() => {
    const timeSinceClick = Date.now() - lastClickTimeRef.current;
    if (timeSinceClick > CLICK_DEBOUNCE_MS) {
      setCurrentState(getStateFromStatus(task.status, taskDefinition.states));
    }
  }, [task.status, taskDefinition.states]);

  // empty string will cause tooltip to not show
  const title = small ? <TaskTooltip taskDefinition={taskDefinition} currentState={currentState} history={task.history} /> : "";

  const IconComponent = (Icons as any)[taskDefinition.icon];
  const colors = {
    backgroundColor: currentState.colour,
    color: "black",
  };

  // want tooltip at top level otherwise we can get weird layout shift
  return (
    <>
      <Tooltip arrow title={title} disableHoverListener={Boolean(menuAnchor)}>
        <ButtonBase
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); incrementStatus(); }}
          onContextMenu={(e) => {
            // Right-click to open state selection menu
            e.preventDefault();
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
          }}
          css={styles.taskItem(small)}
          sx={{ ...colors, position: "relative", opacity: isLoading ? 0.7 : 1 }}
          disabled={disabled || isLoading}
        >
          {isLoading && (
            <CircularProgress
              size={20}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                marginTop: "-10px",
                marginLeft: "-10px",
                color: "black",
              }}
            />
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, opacity: isLoading ? 0.3 : 1 }}>
            <IconComponent style={{ color: "black" }} />
            {!small && (
              <Box sx={{ display: "inline-block" }}>
                <Text variant={currentState.name === "not_requested" ? "button" : "buttonSmall"}>{t(`tasks.${taskDefinition.name}`)}</Text>
                {currentState.name !== "not_requested" && <Text bold variant="buttonSmall">{t(`tasks.${currentState.name}`)}</Text>}
              </Box>
            )}
          </Box>
        </ButtonBase>
      </Tooltip>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        slotProps={{
          root: {
            onContextMenu: (e: React.MouseEvent) => {
              e.preventDefault();
              handleMenuClose();
            },
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {sortedStates.map((state) => (
          <MenuItem
            key={state.id}
            onClick={() => handleStateSelect(state)}
            selected={state.id === currentState.id}
            sx={{
              backgroundColor: state.id === currentState.id ? state.colour : "transparent",
              "&:hover": {
                backgroundColor: state.colour,
                opacity: 0.8,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: 1,
                  backgroundColor: state.colour,
                }}
              />
              <Text>{t(`tasks.${state.name}`)}</Text>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default PatientTaskButton;